import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';

import Link, { StyledLink } from './Link';
import { HEADER_VARIABLES } from './Header';

import DropdownArrow from '../assets/dropdownArrow.inline.svg';

export interface ILanguageLinks {
  currentLanguageCompactLabel: string;
  languages: {
    label: string;
    url: string;
    language: string;
    flagImage: Pick<GatsbyImageProps, 'image' | 'alt'> | null;
  }[];
}

const Container = styled.li`
  ${({ theme: { mq } }) => css`
    display: flex;
    position: relative;

    &:hover {
      > ul {
        opacity: 1;
        pointer-events: initial;
      }

      > span {
        border-bottom-right-radius: 0;
      }
    }

    ${mq.md} {
      margin-left: -1em;
      margin-right: 2em;
      margin-bottom: 40em;
    }
  `}
`;

const PrimaryNonLink = styled.span`
  ${StyledLink.__emotion_styles}

  ${({ theme: { durations } }) => css`
    display: flex;
    position: relative;
    column-gap: 1.25rem;
    align-items: center;
    transition: all ${durations.regular};
    margin-left: 2rem;
    border-radius: 7.25rem;
    cursor: pointer;
    padding: 1.125rem 2rem;

    &::after {
      display: block;
      position: absolute;
      opacity: 0.5;
      margin-left: -2rem;
      border: 0.375rem solid var(${HEADER_VARIABLES.textColor});
      border-radius: inherit;
      width: 100%;
      height: 100%;
      content: '';
      pointer-events: none;
    }

    & > svg {
      width: 1.5rem;
      aspect-ratio: 1 / 1;

      & > path {
        fill: var(${HEADER_VARIABLES.textColor});
      }
    }
  `}
`;

const ListContainer = styled.ul`
  ${({ theme: { colors, mq } }) => css`
    display: flex;
    position: absolute;
    top: calc(100%);
    right: 0;
    flex-direction: column;
    transition: 0.3s;
    opacity: 0;
    border-radius: 1rem 0 1rem 1rem;
    box-shadow: 0 0 2.5rem 0 ${colors.black}12;
    background-color: ${colors.white};
    padding: 1rem 0;
    min-width: 29.75rem;
    list-style: none;
    pointer-events: none;

    ${mq.md} {
      min-width: 0;
      left: 5em;
      right: -0.6em;
      top: calc(105%);
      background: none;
    }
  `}
`;

const ListLink = styled(Link)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    column-gap: 2.5rem;
    align-items: center;
    padding: 1.5rem 4rem;
    line-height: 1.214;
    color: ${colors.black};
    font-size: 1.75rem;

    ${tp.useFont(fonts => fonts.Montserrat.regular)};

    &:hover,
    &:active {
      color: ${colors.black};
    }

    &:hover {
      background-color: ${colors.backgroundLight};
      width: auto;
    }
  `}
`;

const ListLinkFlagWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    position: relative;
    border-radius: 0.5rem;
    box-shadow: 0 0 1.5rem 0 ${colors.black}12;
    width: 4rem;
    height: 3rem;
  `}
`;

const ListLinkFlagImage = styled(GatsbyImage)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const LanguageLinks: React.FC<ILanguageLinks> = ({ currentLanguageCompactLabel, languages }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const listContainerRef = useRef<HTMLUListElement>(null);

  const toggleDropdown = () => {
    setShowDropdown(prevState => {
      return !prevState;
    });

    if (window.innerWidth <= parseInt('958', 10)) {
      setTimeout(() => {
        if (listContainerRef.current) {
          const parentElement = listContainerRef.current.parentElement;
          if (parentElement) {
            const grandParentElement = parentElement.parentElement;
            if (grandParentElement) {
              grandParentElement.scrollTop = grandParentElement.scrollHeight;
            }
          }
        }
      }, 0);
    }
  };

  return (
    <Container>
      <PrimaryNonLink onClick={toggleDropdown}>
        {currentLanguageCompactLabel}
        <DropdownArrow />
      </PrimaryNonLink>
      <ListContainer
        ref={listContainerRef}
        style={{ opacity: showDropdown ? 1 : 0, pointerEvents: showDropdown ? 'initial' : 'none' }}
      >
        {languages.map(({ language, label, url, flagImage }) => (
          <ListLink key={language} href={url}>
            <ListLinkFlagWrapper>
              {flagImage ? <ListLinkFlagImage {...flagImage} /> : null}
            </ListLinkFlagWrapper>
            {label}
          </ListLink>
        ))}
      </ListContainer>
    </Container>
  );
};

export default LanguageLinks;
